import * as d3 from 'd3';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';

import _ from 'lodash';
import { twMerge } from 'tailwind-merge';
import { HoldingResponse, ReportResponse } from '../../../../server/response/responseScheme';
import { apiClient } from '../../../utils/apiClient';
import { useMobxInstance } from '../../../utils/mobxUtil';
import stringUtil from '../../../utils/stringUtil';
import currencyStore from '../../../stores/currencyStore';

export type TargetField = 'weight' | 'amount' | 'valueRank' | 'value';

type HoldingMeta = {
    /**
     * 표시할 연도
     * 전체 분기에 한 번만 표시한다
     */
    displayYear: string;
    hasHolding: boolean;
    emptyCount: number;
    isLatestReport: boolean;
    isOldestReport: boolean;
    isAllSold: boolean;
    isNewHolding: boolean;
};

class ViewStore {
    readonly institutionSlug: string;
    readonly securityHash: string;

    holdings: HoldingResponse[] = [];
    holdingsMeta: HoldingMeta[] = [];
    holdingsByYear: Record<string, Record<string, HoldingResponse>> = {};

    reportYearQuarters: [number, number[]][] = [];

    constructor(institutionSlug: string, securityHash: string) {
        this.institutionSlug = institutionSlug;
        this.securityHash = securityHash;

        makeAutoObservable(this);
    }

    async fetch() {
        if (!this.institutionSlug || !this.securityHash) {
            return;
        }

        const [securityHistory, reportList] = await Promise.all([
            apiClient.report.public.getHoldingsBySecurity.$query({
                institutionSlug: this.institutionSlug,
                securityHash: this.securityHash,
            }),
            apiClient.report.public.getReportList.$query({
                institutionSlug: this.institutionSlug,
            }),
        ]);

        // 리포트 전체 기간에서 종목의 히스토리를 보여주기 위해, 전체 리포트 목록을 가져옴
        // 아래 형식으로 저장
        // [
        //   [year, [quarter, quarter, ...]],
        //   [year, [quarter, quarter, ...]]
        //   ...
        // ]
        const reportYearQuarters = _.chain(reportList)
            .groupBy('reportYear')
            .map((quarters, year) => [Number(year), _.map(quarters, 'reportQuarter')])
            .sortBy(([year]) => Number(year) * -1)
            .value() as [number, number[]][];

        /**
         * 종목의 연도-분기별 그룹
         *
         * 예시:
         * {
         *  2024: {
         *    1: HoldingResponse,
         *    2: HoldingResponse,
         *    3: HoldingResponse,
         *    4: HoldingResponse,
         *  }
         * }
         */
        const holdingsByYear = _.chain(securityHistory)
            .groupBy('reportYear')
            .mapValues((holdings) => _.keyBy(holdings, 'reportQuarter'))
            .value();

        runInAction(() => {
            this.holdings = securityHistory;
            this.holdingsByYear = holdingsByYear;
            this.reportYearQuarters = reportYearQuarters;

            // 리포트는 시간 역순임
            // @@@ holdingMeta 로직 수정 필요
            this.holdingsMeta = _.map(this.holdings, (holding, i) => {
                const hasHolding = !!holding.hash;
                const isLatestReport = i === 0;
                const isOldestReport = i === this.holdings.length - 1;
                const hasHoldingInPrevQuarter = !!this.holdings[i + 1]?.hash;
                const hasHoldingInNextQuarter = !!this.holdings[i - 1]?.hash;
                const isAllSold = !hasHolding && hasHoldingInPrevQuarter;
                const isNewHolding = hasHolding && !isOldestReport && !hasHoldingInPrevQuarter;
                const nextHoldingYear = this.holdings[i - 1]?.reportYear;
                // @@@ 연도 로직을 다시 작성해야 함
                // 그냥 뷰에서 groupby 로 보여주면 좋을 듯
                const displayYear =
                    hasHoldingInNextQuarter && holding.reportYear === nextHoldingYear
                        ? ''
                        : String(holding.reportYear);

                return {
                    displayYear,
                    hasHolding,
                    emptyCount: 0,
                    isOldestReport,
                    isLatestReport,
                    isAllSold,
                    isNewHolding,
                };
            });
        });
    }
}

const SecurityHistory = observer(
    ({
        institutionSlug,
        securityHash,
        targetField,
    }: {
        institutionSlug: string;
        securityHash: string;
        /**
         * 표시할 데이터 필드
         */
        targetField: TargetField;
    }) => {
        const viewStore = useMobxInstance(ViewStore, institutionSlug, securityHash);

        useEffect(() => {
            viewStore.fetch();
        }, [institutionSlug]);

        const scaleFn = useMemo(() => {
            const max = d3.max(viewStore.holdings, (holding) => Number(holding[targetField])) || 0;
            return d3.scaleLinear().domain([0, max]).range([0, 100]);
        }, [viewStore.holdings, targetField]);

        return (
            <div className="flex flex-col gap-2">
                {viewStore.reportYearQuarters.map(([year, quarters]) => {
                    const yearHoldings = viewStore.holdingsByYear[year];
                    return (
                        <div key={year} className="flex flex-row justify-between gap-1">
                            <div className="flex flex-col justify-start">
                                <div className="text-base-content/60 flex h-6 shrink-0 items-center text-sm tabular-nums">
                                    {year}
                                </div>
                            </div>
                            <div className="flex flex-grow flex-col gap-2">
                                {_.isEmpty(yearHoldings) && (
                                    <div className="flex h-6 w-full items-center">
                                        <div className="bg-base-300/70 ml-1 h-[1px] w-full"></div>
                                    </div>
                                )}
                                {!_.isEmpty(yearHoldings) &&
                                    _.map(quarters, (quarter) => {
                                        const holding = yearHoldings[quarter];

                                        return (
                                            <div key={quarter} className="flex flex-row gap-2">
                                                <div className="text-base-content/60 flex h-6 shrink-0 items-center text-sm tabular-nums">
                                                    {quarter}Q
                                                </div>
                                                {!holding && (
                                                    <div className="flex h-6 w-full items-center">
                                                        <div className="bg-base-300/70 h-[1px] w-full"></div>
                                                    </div>
                                                )}
                                                {holding && targetField === 'weight' && (
                                                    <WeightRow
                                                        holding={holding}
                                                        scaleFn={scaleFn}
                                                        targetField={targetField}
                                                    />
                                                )}
                                                {holding && targetField === 'value' && (
                                                    <ValueRow
                                                        holding={holding}
                                                        scaleFn={scaleFn}
                                                        targetField={targetField}
                                                    />
                                                )}
                                                {holding && targetField === 'valueRank' && (
                                                    <ValueRankRow
                                                        holding={holding}
                                                        scaleFn={scaleFn}
                                                        targetField={targetField}
                                                    />
                                                )}
                                                {holding && targetField === 'amount' && (
                                                    <AmountRow
                                                        holding={holding}
                                                        scaleFn={scaleFn}
                                                        targetField={targetField}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    },
);

const WeightRow = observer(
    ({
        holding,
        scaleFn,
        targetField,
    }: {
        holding: HoldingResponse;
        scaleFn: (value: number) => number;
        targetField: TargetField;
    }) => {
        return (
            <div className="relative flex h-6 w-full items-center justify-between overflow-hidden">
                <div
                    className={twMerge(
                        'bg-base-content/30 absolute top-0 left-0 z-0 h-full rounded-xs',
                    )}
                    style={{
                        width: `${scaleFn(Number(holding[targetField]))}%`,
                    }}
                ></div>
                <div className="text-primary z-10 flex items-center px-1 text-xs"></div>
                <div className={'text-base-content z-10 shrink-0 text-sm tabular-nums'}>
                    {stringUtil.toPercentage(holding[targetField])}
                </div>
            </div>
        );
    },
);

const ValueRow = observer(
    ({
        holding,
        scaleFn,
        targetField,
    }: {
        holding: HoldingResponse;
        scaleFn: (value: number) => number;
        targetField: TargetField;
    }) => {
        return (
            <div className="relative flex h-6 w-full items-center justify-between overflow-hidden">
                <div
                    className={twMerge(
                        'bg-base-content/30 absolute top-0 left-0 z-0 h-full rounded-xs',
                    )}
                    style={{
                        width: `${scaleFn(Number(holding[targetField]))}%`,
                    }}
                ></div>
                <div className="text-primary z-10 flex items-center px-1 text-xs"></div>
                <div className={'text-base-content z-10 shrink-0 text-sm tabular-nums'}>
                    {currencyStore.toCompactUsdOrKrw(Number(holding[targetField]) * 1000)}
                </div>
            </div>
        );
    },
);

const AmountRow = observer(
    ({
        holding,
        scaleFn,
        targetField,
    }: {
        holding: HoldingResponse;
        scaleFn: (value: number) => number;
        targetField: TargetField;
    }) => {
        return (
            <div className="relative flex h-6 w-full items-center justify-between overflow-hidden">
                <div
                    className={twMerge(
                        'bg-base-content/30 absolute top-0 left-0 z-0 h-full rounded-xs',
                    )}
                    style={{
                        width: `${scaleFn(Number(holding[targetField]))}%`,
                    }}
                ></div>
                <div className="text-primary z-10 flex items-center px-1 text-xs"></div>
                <div className={'text-base-content z-10 shrink-0 text-sm tabular-nums'}>
                    {stringUtil.toCommaNumber(holding[targetField])}주
                </div>
            </div>
        );
    },
);

const ValueRankRow = observer(
    ({
        holding,
        scaleFn,
        targetField,
    }: {
        holding: HoldingResponse;
        scaleFn: (value: number) => number;
        targetField: TargetField;
    }) => {
        // 순위는 원을 위치시키는 방식이라서 최소값을 1위로 설정
        // 분기마다 종목의 순위가 다르기 때문에 스케일을 행마다 설정한다
        const valueRankScale = useMemo(() => {
            return d3
                .scaleLinear()
                .domain([1, holding.totalHolding as number])
                .range([0, 100]);
        }, [holding.totalHolding]);

        return (
            <>
                <div className="relative flex h-6 w-full items-center justify-between overflow-hidden px-2">
                    <div className="relative h-full w-full">
                        <div
                            className={twMerge(
                                'absolute top-1/2 z-10 h-2 w-2 -translate-y-1/2 rounded-full bg-neutral-500',
                            )}
                            style={{
                                left: `${valueRankScale(Number(holding[targetField]))}%`,
                            }}
                        ></div>
                        <div className="bg-base-content/30 absolute top-1/2 left-0 h-[1px] w-full -translate-y-1/2"></div>
                    </div>
                </div>
                <div
                    className={twMerge('text-base-content z-10 flex shrink-0 items-center text-xs')}
                >
                    {stringUtil.toCommaNumber(holding[targetField])}위/
                    <span className="text-base-content/60 tabular-nums">
                        {stringUtil.toCommaNumber(holding.totalHolding)}
                    </span>
                </div>
            </>
        );
    },
);

export default SecurityHistory;
